
import { useEffect, useRef, useState } from "react";
import { useActivity } from "../../../contexts/ActivityProvider";
import { useCourse } from "../../../contexts/CourseProvider";
import Loading from "../../general/Loading";

import { getItem } from "../../../utils/persistanceStorage";
import { getCurrUnixDate } from "../../../utils/date";
import xapiverbs from "../../../config/xapiverbs";
import { saveEvent } from "../../../api/userevents";
import { processSpliceActivity } from "../../../api/postactivity";

const SPLICE_ORIGIN = "https://acos.cs.vt.edu";

const SpliceChallenge = () => {
  const { activity, activityData } = useActivity();
  const { course, updateConceptScores } = useCourse();

  const [loadingIframe, setLoadingIframe] = useState(true);
  const iframeRef = useRef(null);

  const activityid = activity.activityid;
  const courseid = course.courseid;

  // Keep track of the current activity session.
  const startedTimestamp = useRef(getCurrUnixDate());

  // Deduplicate provider messages.
  const seenMessages = useRef(new Set());

  // Process scored events sequentially to preserve order.
  const processingQueue = useRef(Promise.resolve());

  // Keep the latest score-refresh function available.
  const updateScoresRef = useRef(updateConceptScores);

  useEffect(() => {
    updateScoresRef.current = updateConceptScores;
  }, [updateConceptScores]);

  useEffect(() => {
    // Reset state when the activity changes.
    seenMessages.current.clear();
    startedTimestamp.current = getCurrUnixDate();
    processingQueue.current = Promise.resolve();

    let active = true;

    const handleScoredEvent = (message) => {
      const userData = getItem("user-data");

      const processObject = {
        // Existing backend fields
        activityid,
        courseid,
        sessionid: userData?.sessionid,
        startedTimestamp: startedTimestamp.current,
        endedTimestamp: getCurrUnixDate(),
        score: message.state?.points ?? message.score,
        total: message.state?.max_points ?? null,

        // SPLICE metadata for item-level processing
        provider: "splice",
        message_id: message.message_id,
        event_data: message.state?.event_data ?? null,
      };

      // Preserve ordering of score-processing requests.
      processingQueue.current = processingQueue.current
        .catch(() => {
          // Allow later events to proceed after a failed request.
        })
        .then(async () => {
          const result = await processSpliceActivity(processObject);

          if (active) {
            await Promise.resolve(updateScoresRef.current());
          }

          return result;
        })
        .catch((error) => {
          console.error("[SPLICE] Score processing failed:", error);
        });
    };

    const handler = (event) => {

      // Only accept messages from our SPLICE iframe.
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.origin !== SPLICE_ORIGIN) return;

      const message = event.data;
      const subject = message?.subject;

      if ( subject !== "SPLICE.sendEvent" && subject !== "SPLICE.reportScoreAndState" ) { return; }

      // // Ignore duplicate provider messages.
      // if (message.message_id) {
      //   if (seenMessages.current.has(message.message_id)) return;
      //   seenMessages.current.add(message.message_id);
      // }

      if (subject === "SPLICE.sendEvent") {
        const payload = { provider: "splice", subject, message_id: message.message_id, ...(message.data || {}), };

        saveEvent( activityid, courseid, xapiverbs.INTERACTED, payload ).catch((error) => {
          console.error("[SPLICE] Event logging failed:", error); });

        return;
      }

      if (subject === "SPLICE.reportScoreAndState") {
        const payload = {
          provider: "splice",
          subject,
          message_id: message.message_id,
          score: message.score,
          points: message.state?.points,
          max_points: message.state?.max_points,
          event_data: message.state?.event_data,
        };

        console.log("SPLICE scored event: ", payload);

        // Save the original scored observation.
        saveEvent(
          activityid,
          courseid,
          xapiverbs.ANSWERED,
          payload
        ).catch((error) => {
          console.error("[SPLICE] Score event logging failed:", error);
        });

        // Forward the scored outcome to existing backend processing.
        handleScoredEvent(message);
      }
    };

    window.addEventListener("message", handler);

    return () => {
      active = false;
      window.removeEventListener("message", handler);
    };
  }, [activityid, courseid]);

  return (
    <div className="p-4">
      <div className="mb-2">{activityData.description}</div>

      <div className="relative h-full w-full pt-[40%] overflow-hidden my-4">
        {loadingIframe && <Loading />}

        <iframe
          title="web content"
          ref={iframeRef}
          src={activityData.url || undefined}
          className="w-full h-full absolute top-0 bottom-0 right-0 left-0"
          onLoad={() => setLoadingIframe(false)}
        />
      </div>
    </div>
  );
};

export default SpliceChallenge;
